/**
 * Painel de vendas.
 *
 * "Venda" é uma placa aderida, datada pela data de cadastro, e o número que
 * vale é o LÍQUIDO: adesões menos as que saíram da carteira. Bruto e cancelado
 * aparecem ao lado para a diferença ficar visível.
 *
 * Cooperativa é o eixo principal de agrupamento, por decisão da diretoria.
 */

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CarFront,
  Info,
  PieChart,
  ShieldCheck,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { competenciaAtual, diaMes, moedaCompacta, moedaExata, numero } from '../lib/format.ts';
import { Aviso, Botao, Card, Esqueleto, Segmentado, TituloSecao } from '../components/ui/index.tsx';
import { CartaoDado, GradeBento, type Variacao } from '../components/ui/Bento.tsx';
import Folha from '../components/ui/Folha.tsx';
import PainelMetas from '../components/metas/PainelMetas.tsx';
import {
  AreaTempo,
  EvolucaoBase,
  RankingBarras,
  RoscaMix,
} from '../components/charts/index.tsx';
import BarraFiltros, {
  filtroInicial,
  type OpcoesFiltro,
  type ValorFiltro,
} from '../components/filtros/BarraFiltros.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

interface Agrupado {
  codigo: string;
  rotulo: string;
  valor: number;
  fipe: number;
}

interface ResumoVendas {
  periodo: { de: string; ate: string; granularidade: 'dia' | 'mes' };
  placas: { brutas: number; canceladas: number; baseAtiva: number; saldo: number };
  fipeProtegida: number;
  meta: { placas: number; realizado: number; percentual: number } | null;
  serie: { rotulo: string; valor: number }[];
  porConsultor: Agrupado[];
  porCooperativa: Agrupado[];
  porTipo: { rotulo: string; valor: number }[];
  espelhoVazio: boolean;
}

interface Ticket {
  geral: number;
  receitaMensalMedia: number;
  placasAtivas: number;
  recebido: number;
  inadimplencia: number;
  porTipo: Dimensao[];
  porEstado: Dimensao[];
  porCooperativa: Dimensao[];
  porConsultor: Dimensao[];
}

interface Dimensao {
  rotulo: string;
  valor: number;
  placas: number;
  receita: number;
}

interface Evolucao {
  meses: {
    mes: string;
    recebido: number;
    inadimplencia: number;
    aReceber: number;
    adesoes: number;
    inativacoes: number;
    baseAtiva: number;
  }[];
}

interface Regioes {
  cidades: { rotulo: string; valor: number }[];
  estados: { rotulo: string; valor: number }[];
}

/** Monta a query string a partir do filtro, omitindo o que está vazio. */
export function paramsDe(f: ValorFiltro): string {
  const p = new URLSearchParams({ de: f.de, ate: f.ate });
  if (f.cooperativa) p.set('cooperativa', f.cooperativa);
  if (f.consultor) p.set('consultor', f.consultor);
  return p.toString();
}

export default function Vendas() {
  const { usuario, pode } = useAuth();
  const [filtro, setFiltro] = useState<ValorFiltro>(filtroInicial);
  const [metasAbertas, setMetasAbertas] = useState(false);
  const fecharMetas = useCallback(() => setMetasAbertas(false), []);
  const qs = paramsDe(filtro);

  const { data: opcoes } = useQuery({
    queryKey: ['vendas', 'filtros'],
    queryFn: () => api.get<OpcoesFiltro>('/vendas/filtros'),
    staleTime: 10 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['vendas', 'resumo', qs],
    queryFn: () => api.get<ResumoVendas>(`/vendas/resumo?${qs}`),
    // Atualiza sozinho: ninguém deve apertar botão para ver o número do dia.
    // O worker sincroniza a cada 15 min; enquanto o espelho está vazio, olha
    // mais de perto para a primeira carga aparecer assim que chegar.
    refetchInterval: (q) => (q.state.data?.espelhoVazio ? 15_000 : 60_000),
  });

  const { data: ticket } = useQuery({
    queryKey: ['vendas', 'ticket', qs],
    queryFn: () => api.get<Ticket>(`/vendas/ticket?${qs}`),
  });

  // Tendência: sempre os 12 meses que terminam no fim do período. No recorte
  // padrão ("este mês") a evolução do próprio período seria um ponto só — sem
  // história para dizer se o número de hoje é bom ou ruim.
  const qsTendencia = paramsDe({ ...filtro, de: dozeMesesAntes(filtro.ate) });
  const { data: tendencia } = useQuery({
    queryKey: ['vendas', 'evolucao', qsTendencia],
    queryFn: () => api.get<Evolucao>(`/vendas/evolucao?${qsTendencia}`),
  });

  const { data: regioes } = useQuery({
    queryKey: ['vendas', 'regioes', qs],
    queryFn: () => api.get<Regioes>(`/vendas/regioes?${qs}`),
  });

  const eixoMensal = data?.periodo.granularidade === 'mes';

  /** Área de tendência e pílula de variação para um cartão de dado. */
  const comTendencia = (
    campo: 'baseAtiva' | 'adesoes' | 'inativacoes',
    extra?: Pick<Tendencia, 'fluxo' | 'subirEhRuim'>,
  ): { tendencia?: number[]; variacao?: Variacao | null } => {
    if (!tendencia) return {};
    const t: Tendencia = {
      meses: tendencia.meses.map((m) => m.mes),
      valores: tendencia.meses.map((m) => m[campo]),
      ...extra,
    };
    const v = variacao(t);
    return {
      tendencia: t.valores,
      variacao:
        v && Math.round(v.pct) !== 0
          ? {
              texto: `${v.pct > 0 ? '▲' : '▼'} ${Math.abs(Math.round(v.pct))}%`,
              detalhe: v.rotulo,
              bom: t.subirEhRuim ? v.pct < 0 : v.pct > 0,
            }
          : null,
    };
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[13px] text-[var(--muted)]">
            {usuario?.papel === 'consultor' ? 'Minhas vendas' : 'Vendas'}
          </p>
          <h2 className="text-[26px] font-semibold text-[var(--fg)]">Painel</h2>
        </div>
        {/* Metas moravam numa tela própria; agora são uma ação da gestão
            aqui, e o progresso de cada um aparece no Início. */}
        {pode('admin', 'diretor', 'gerente_regional', 'financeiro') && (
          <Botao icone={<Target size={15} />} onClick={() => setMetasAbertas(true)}>
            Metas
          </Botao>
        )}
      </div>

      <Folha aberta={metasAbertas} titulo="Metas do mês" onFechar={fecharMetas}>
        <PainelMetas />
      </Folha>

      <BarraFiltros valor={filtro} onChange={setFiltro} opcoes={opcoes} />

      {data?.espelhoVazio && (
        <Aviso tom="alerta" icone={<Info size={14} />}>
          Primeira sincronização com a Hinova em andamento — esta tela se atualiza sozinha quando os
          dados chegarem.
        </Aviso>
      )}

      {isLoading ? (
        <Carregando />
      ) : data ? (
        <>
          {/* Bento: a carteira é o herói (2×2 no desktop) e os quatro
              números do período encaixam ao lado, 2×2. No celular o herói
              ocupa a largura e os demais ficam em pares. */}
          <GradeBento>
            <CartaoDado
              tamanho="destaque"
              tom="marca"
              Icone={CarFront}
              rotulo="Placas ativas"
              valor={numero(data.placas.baseAtiva)}
              nota="carteira no fim do período · 12 meses"
              {...comTendencia('baseAtiva')}
            />
            <CartaoDado
              tom="sucesso"
              Icone={TrendingUp}
              rotulo="Adesões"
              valor={numero(data.placas.brutas)}
              {...comTendencia('adesoes', { fluxo: true })}
            />
            <CartaoDado
              tom={data.placas.canceladas > 0 ? 'perigo' : 'neutro'}
              Icone={TrendingDown}
              rotulo="Cancelamentos"
              valor={numero(data.placas.canceladas)}
              {...comTendencia('inativacoes', { fluxo: true, subirEhRuim: true })}
            />
            <CartaoDado
              tom="marca"
              Icone={Wallet}
              rotulo="Ticket médio"
              valor={ticket ? moedaExata(ticket.geral) : '—'}
              nota="mensalidade por placa"
            />
            <CartaoDado
              Icone={ShieldCheck}
              rotulo="FIPE protegida"
              valor={moedaCompacta(data.fipeProtegida)}
              nota="valor dos veículos aderidos"
            />
          </GradeBento>

          {/* Recebido, inadimplência e o caixa mês a mês moram no Financeiro
              (Carteira); a meta, no Início. Repetidos aqui, eram o mesmo número
              em duas telas — e às vezes com recortes diferentes. */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <TituloSecao>{eixoMensal ? 'Adesões por mês' : 'Adesões por dia'}</TituloSecao>
              <AreaTempo
                dados={data.serie.map((s) => ({
                  rotulo: eixoMensal ? mesCurto(s.rotulo) : diaMes(s.rotulo),
                  valor: s.valor,
                }))}
              />
            </Card>

            <Card className="p-5">
              <TituloSecao>
                <span className="inline-flex items-center gap-2">
                  <Users size={14} /> Evolução da base · 12 meses
                </span>
              </TituloSecao>
              {tendencia ? (
                <EvolucaoBase
                  meses={tendencia.meses.map((m) => ({
                    rotulo: mesCurto(m.mes),
                    baseAtiva: m.baseAtiva,
                    adesoes: m.adesoes,
                    inativacoes: m.inativacoes,
                  }))}
                />
              ) : (
                <Esqueleto className="h-52" />
              )}
            </Card>
          </div>

          {/* Um ranking só, com dimensão e medida escolhíveis, no lugar de
              sete cartões quase iguais em sequência — no celular eram
              metros de rolagem para achar o que se queria comparar. */}
          <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
            <RankingVendas
              resumo={data}
              ticket={ticket}
              regioes={regioes}
              cooperativaFiltrada={filtro.cooperativa}
              meuCodigo={usuario?.codigoVoluntario ?? null}
            />

            <Card className="p-5">
              <TituloSecao>
                <span className="inline-flex items-center gap-2">
                  <PieChart size={14} /> Tipo de veículo
                </span>
              </TituloSecao>
              <RoscaMix fatias={data.porTipo} />
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ─────────────────────────── Peças ───────────────────────────

type Medida = 'placas' | 'ticket' | 'fipe';
type Recorte = 'cooperativa' | 'consultor' | 'tipo' | 'estado' | 'cidade';

const MEDIDAS: { valor: Medida; rotulo: string }[] = [
  { valor: 'placas', rotulo: 'Placas' },
  { valor: 'ticket', rotulo: 'Ticket' },
  { valor: 'fipe', rotulo: 'FIPE' },
];

const NOME_DIMENSAO: Record<Recorte, string> = {
  cooperativa: 'Cooperativa',
  consultor: 'Consultor',
  tipo: 'Tipo',
  estado: 'Estado',
  cidade: 'Cidade',
};

/**
 * Quais recortes existem para cada medida. Só aparece o que a API calcula —
 * opção que leva a "sem dados" ensina a pessoa a não confiar no seletor.
 * Placas por tipo fica de fora porque é o gráfico de tipo ao lado.
 */
const DIMENSOES: Record<Medida, Recorte[]> = {
  placas: ['cooperativa', 'consultor', 'estado', 'cidade'],
  ticket: ['cooperativa', 'consultor', 'tipo', 'estado'],
  fipe: ['cooperativa', 'consultor'],
};

/** Quantos aparecem antes de "ver todos". */
const TOPO_RANKING = 10;

function RankingVendas({
  resumo,
  ticket,
  regioes,
  cooperativaFiltrada,
  meuCodigo,
}: {
  resumo: ResumoVendas;
  ticket: Ticket | undefined;
  regioes: Regioes | undefined;
  cooperativaFiltrada: string | undefined;
  meuCodigo: string | null;
}) {
  const [medida, setMedida] = useState<Medida>('placas');
  const [escolhida, setDimensao] = useState<Recorte>('cooperativa');
  const [todos, setTodos] = useState(false);

  // Trocar para uma medida que não tem o recorte atual volta para cooperativa,
  // em vez de deixar um seletor apontando para nada.
  const dimensao = DIMENSOES[medida].includes(escolhida) ? escolhida : 'cooperativa';

  const agrupado = (lista: Agrupado[], campo: 'valor' | 'fipe', destaque?: string | null) =>
    lista.map((a) => ({ rotulo: a.rotulo, valor: a[campo], destaque: !!destaque && a.codigo === destaque }));

  const itens: ItemRankingVendas[] | undefined = {
    placas: {
      cooperativa: agrupado(resumo.porCooperativa, 'valor', cooperativaFiltrada),
      consultor: agrupado(resumo.porConsultor, 'valor', meuCodigo),
      estado: regioes?.estados,
      cidade: regioes?.cidades,
      tipo: undefined,
    },
    ticket: {
      cooperativa: ticket?.porCooperativa,
      consultor: ticket?.porConsultor,
      tipo: ticket?.porTipo,
      estado: ticket?.porEstado,
      cidade: undefined,
    },
    fipe: {
      cooperativa: agrupado(resumo.porCooperativa, 'fipe', cooperativaFiltrada),
      consultor: agrupado(resumo.porConsultor, 'fipe', meuCodigo),
      tipo: undefined,
      estado: undefined,
      cidade: undefined,
    },
  }[medida][dimensao];

  const ordenados = itens ? [...itens].sort((a, b) => b.valor - a.valor) : undefined;
  const formatar = medida === 'ticket' ? moedaExata : medida === 'fipe' ? moedaCompacta : undefined;

  return (
    <Card className="min-w-0 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="inline-flex items-center gap-2 text-sm font-medium text-[var(--fg)]">
          <Trophy size={14} /> Ranking
        </h3>
        <Segmentado rotulo="Medida" opcoes={MEDIDAS} valor={medida} onChange={setMedida} />
      </div>

      <div className="scroll-x -mx-5 mb-4 px-5">
        <Segmentado
          rotulo="Agrupar por"
          opcoes={DIMENSOES[medida].map((d) => ({ valor: d, rotulo: NOME_DIMENSAO[d] }))}
          valor={dimensao}
          onChange={setDimensao}
        />
      </div>

      {ordenados ? (
        <>
          <RankingBarras
            itens={todos ? ordenados : ordenados.slice(0, TOPO_RANKING)}
            formatar={formatar}
            rotuloColuna={NOME_DIMENSAO[dimensao]}
            rotuloMedida={MEDIDAS.find((m) => m.valor === medida)!.rotulo}
          />
          {ordenados.length > TOPO_RANKING && (
            <button
              onClick={() => setTodos((v) => !v)}
              className="anel-foco mt-3 h-11 w-full rounded-[var(--r-md)] text-[13px] font-medium text-[var(--muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]"
            >
              {todos ? `Mostrar só os ${TOPO_RANKING} primeiros` : `Ver todos os ${ordenados.length}`}
            </button>
          )}
        </>
      ) : (
        <Esqueleto className="h-40" />
      )}
    </Card>
  );
}

interface ItemRankingVendas {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}

/**
 * Série de 12 meses de um indicador.
 *
 * `fluxo` é o que se acumula dentro do mês (adesões, saídas): o mês corrente
 * ainda está pela metade, então a comparação usa os dois últimos meses
 * FECHADOS — senão todo início de mês pareceria uma queda. Estoque (a base
 * ativa) compara o mês corrente com o anterior.
 */
interface Tendencia {
  meses: string[];
  valores: number[];
  fluxo?: boolean;
  /** Para cancelamentos: subir é a notícia ruim. */
  subirEhRuim?: boolean;
}

function variacao(t: Tendencia): { pct: number; rotulo: string } | null {
  const corrente = t.meses.at(-1)?.startsWith(competenciaAtual());
  const fim = t.fluxo && corrente ? t.valores.length - 1 : t.valores.length;
  if (fim < 2) return null;
  const atual = t.valores[fim - 1]!;
  const antes = t.valores[fim - 2]!;
  if (antes === 0) return null;
  const nome = (iso: string) =>
    new Date(`${iso.slice(0, 7)}-15T12:00:00`).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return {
    pct: ((atual - antes) / antes) * 100,
    rotulo: `${nome(t.meses[fim - 1]!)} vs ${nome(t.meses[fim - 2]!)}`,
  };
}

function Carregando() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} className="h-[100px]" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Esqueleto className="h-[220px]" />
        <Esqueleto className="h-[220px] lg:col-span-2" />
      </div>
    </div>
  );
}

/** Primeiro dia do 11º mês antes do de `ate` — com ele, 12 meses fechados. */
function dozeMesesAntes(ate: string): string {
  const [ano, mes] = ate.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(ano, mes - 12, 1));
  return d.toISOString().slice(0, 10);
}

/** "ago/26" — cabe no eixo do celular. */
function mesCurto(iso: string): string {
  const [ano, mes] = iso.split('-');
  const d = new Date(Number(ano), Number(mes) - 1, 1);
  return `${d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}/${ano?.slice(2)}`;
}
